import React, { useEffect, useState } from "react";
import { Target, Save, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { cardCls, Btn, Input } from "@/components/admin/ui";

const CATS = ["Operations", "Marketing", "Payroll", "Rent", "Supplies", "Technology", "Professional Fees", "Other"];

const monthOf = (e) => {
  const d = (e.date || "").trim();
  if (d.length >= 7 && d[4] === "-") return d.slice(0, 7);
  return (e.created_at || "").slice(0, 7);
};

export default function BudgetsCard({ expenses = [] }) {
  const [budgets, setBudgets] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setBudgets(data.category_budgets || {})).catch(() => {});
  }, []);

  const thisMonth = new Date().toISOString().slice(0, 7);
  const spentByCat = {};
  expenses.forEach((e) => {
    if (monthOf(e) === thisMonth) {
      spentByCat[e.category] = (spentByCat[e.category] || 0) + Number(e.amount || 0);
    }
  });

  const set = (cat) => (e) => setBudgets((b) => ({ ...b, [cat]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try {
      const clean = {};
      Object.entries(budgets).forEach(([k, v]) => { const n = Number(v); if (n > 0) clean[k] = n; });
      await api.put("/admin/settings", { category_budgets: clean });
      setBudgets(clean);
      toast.success("Budgets saved");
    } catch { toast.error("Could not save budgets"); }
    finally { setSaving(false); }
  };

  const fmt = (n) => "£" + Number(n || 0).toLocaleString("en-GB");
  const monthLabel = new Date().toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  return (
    <div data-testid="budgets-card" className={`${cardCls} p-6 mb-6`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-3">
          <Target size={20} className="text-bd-gold" />
          <h2 className="font-serif text-xl text-bd-ivory">Category budgets</h2>
        </div>
        <Btn data-testid="budgets-save" onClick={save} disabled={saving}><Save size={14} /> Save</Btn>
      </div>
      <p className="text-xs text-bd-ivory/40 mb-5">Set a monthly budget per category. Spend is measured against {monthLabel}.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
        {CATS.map((cat) => {
          const budget = Number(budgets[cat] || 0);
          const spent = spentByCat[cat] || 0;
          const over = budget > 0 && spent > budget;
          const pct = budget > 0 ? Math.min(100, (spent / budget) * 100) : 0;
          return (
            <div key={cat} data-testid={`budget-row-${cat.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-center justify-between gap-3 mb-1.5">
                <span className="text-sm text-bd-ivory/80 flex items-center gap-2">
                  {cat}
                  {over && <span data-testid={`budget-over-${cat.toLowerCase().replace(/\s+/g, "-")}`} className="inline-flex items-center gap-1 text-[0.6rem] uppercase tracking-wider border border-red-400/50 text-red-400 px-1.5 py-0.5"><AlertTriangle size={11} /> Over</span>}
                </span>
                <div className="w-28 flex-shrink-0">
                  <Input data-testid={`budget-input-${cat.toLowerCase().replace(/\s+/g, "-")}`} type="number" min="0" placeholder="—" value={budgets[cat] ?? ""} onChange={set(cat)} className="text-right py-1.5" />
                </div>
              </div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className={over ? "text-red-400" : "text-bd-ivory/50"}>Spent {fmt(spent)}{budget > 0 ? ` of ${fmt(budget)}` : ""}</span>
                {budget > 0 && <span className={over ? "text-red-400" : "text-bd-ivory/40"}>{over ? `${fmt(spent - budget)} over` : `${fmt(budget - spent)} left`}</span>}
              </div>
              <div className="h-1.5 bg-white/10 overflow-hidden">
                <div className={`h-full transition-all ${over ? "bg-red-400" : "bg-bd-gold"}`} style={{ width: `${budget > 0 ? (over ? 100 : pct) : 0}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
